<script setup lang="ts">
/**
 * Компонент для действий с продуктом (добавление в корзину, счетчик)
 * Следует Vue Style Guide: Priority A
 */

import { computed } from 'vue';
import { consola } from 'consola';

interface Props {
  inCart: boolean;
  quantity: number;
  totalPrice: number;
  /** Цена без скидки — показывается зачёркнутой внутри кнопки (комбо) */
  oldPrice?: number | null;
  isLoading?: boolean;
  isDisabled?: boolean;
  disabledMessage?: string;
  step?: number;
  minQuantity?: number;
}

interface Emits {
  (e: 'addToCart'): void;
  (e: 'quantityChange', value: number): void;
  (e: 'openBasket'): void;
}

const props = withDefaults(defineProps<Props>(), {
  oldPrice: null,
  isLoading: false,
  isDisabled: false,
  disabledMessage: '',
  step: 1,
  minQuantity: 0,
});

const emit = defineEmits<Emits>();

/** Старую цену показываем, только если она реально выше текущей */
const hasOldPrice = computed(() => !!props.oldPrice && props.oldPrice > props.totalPrice);

const logger = consola.withTag('ProductActions');

const handleQuantityChange = (newQuantity: number) => {
  logger.info('🔢🔢🔢 ProductActions: handleQuantityChange called 🔢🔢🔢', {
    currentQuantity: props.quantity,
    newQuantity,
    delta: newQuantity - props.quantity,
    inCart: props.inCart,
    isLoading: props.isLoading,
    isDisabled: props.isDisabled,
    timestamp: new Date().toISOString(),
  });

  logger.debug('📤 Emitting quantityChange event', {
    newQuantity,
    eventName: 'quantityChange',
  });

  try {
    emit('quantityChange', newQuantity);
    logger.success('✅✅✅ quantityChange event emitted successfully ✅✅✅', { newQuantity });
  } catch (error: any) {
    logger.error('❌❌❌ Error emitting quantityChange event ❌❌❌', {
      error: error?.message || error,
      stack: error?.stack,
      newQuantity,
    });
  }
};

const handleAddToCart = () => {
  if (!props.isDisabled && !props.isLoading) {
    emit('addToCart');
  }
};

const handleOpenBasket = () => {
  emit('openBasket');
};
</script>

<template>
  <div class="product-actions">
    <!-- Основная кнопка/счетчик -->
    <div class="product-actions__main">
      <!--
        Счётчик, когда товар в корзине.

        Условие НЕ включает `isLoading`: раньше на время запроса счётчик
        размонтировался, на его месте вспыхивала кнопка «В корзину», и он
        возвращался уже с новым числом. Из-за этого не только не работала
        анимация количества (узел пересоздавался), но и сама подмена читалась
        как подёргивание. Теперь счётчик остаётся на месте, а на время запроса
        просто не принимает нажатия — число всё равно меняется сразу, локально.
      -->
      <UiBaseCounter
        v-if="inCart"
        :count-v="quantity"
        :price="totalPrice"
        :min-v="minQuantity"
        :max-v="99"
        :step="step"
        class="w-full"
        :class="{ 'product-actions__counter--busy': isLoading }"
        size="md"
        data-testid="product-modal-counter"
        @count="handleQuantityChange"
      />

      <!-- Кнопка добавления если товара нет в корзине -->
      <UiButtonMain
        v-else
        color="red"
        size="xl"
        block
        class="w-full product-actions__add"
        :loading="isLoading"
        :disabled="isDisabled || isLoading"
        data-testid="product-modal-add-button"
        @click="handleAddToCart"
      >
        <!--
          Цена всегда слотом, а не пропом `label`: она меняется от добавок,
          и в кнопке должно быть видно, что сумма поехала вверх или вниз —
          тем же движением, что и количество в счётчике рядом.

          Скидочная цена живёт здесь же: отдельная строка над кнопкой
          дублировала бы её.
        -->
        <template #default>
          <span class="product-actions__price">
            <UiRollingValue :value="totalPrice" suffix="₽" />
            <s v-if="hasOldPrice" class="product-actions__price-old">{{ oldPrice }}₽</s>
          </span>
        </template>
      </UiButtonMain>
    </div>

    <!-- Кнопка корзины (только mobile) -->
    <UiButtonMain
      icon="heroicons:shopping-cart-solid"
      color="grey"
      class="product-actions__basket"
      size="xl"
      label=""
      :trailing="true"
      @click="handleOpenBasket"
    />
  </div>
</template>

<style scoped lang="scss">
.product-actions {
  /* flex-1 + min-w-0 у основного блока, shrink-0 у корзины — иначе w-full у __main съедает всю строку и вторая кнопка уезжает/схлопывается */
  @apply flex w-full items-center gap-3 pt-5 mt-auto border-t border-b-blue-300;

  @media (max-width: 680px) {
    position: relative;
    z-index: 52;
  }

  &__main {
    @apply min-w-0 flex-1;
  }

  /* Пока запрос в пути, счётчик не принимает нажатия — раньше эту роль играло
     его исчезновение. Прозрачность не трогаем: моргание на каждое нажатие
     заметнее, чем отсутствие отклика на те доли секунды, что идёт запрос,
     а число меняется сразу, ещё до ответа. */
  &__counter--busy {
    pointer-events: none;
  }

  /* Фирменный акцент этой кнопке даёт UiButtonMain (класс .btn-accent) —
     он же красит все остальные основные кнопки приложения. Класс оставлен
     как якорь для тестов и точечных правок вёрстки. */

  &__basket {
    @apply shrink-0 bg-gray-500 md:hidden;
  }

  &__message {
    @apply text-red-500 text-base font-semibold my-2 w-full text-center;
  }

  &__price {
    @apply inline-flex items-baseline gap-2;
  }

  &__price-old {
    @apply text-sm font-normal opacity-70;
  }
}
</style>
